// BFSG
import React from 'react'
import theme from 'theme'
import styled from 'styled-components'
import useDisplayVariant from 'modules/productDetail/hooks/useDisplayVariant'
import ProductImage from 'theme/atoms/ProductImage'
import Flags from 'theme/molecules/Flags'
import useGallery from '../hooks/useGallery'
import WistiaPlayer from 'theme/atoms/WistiaPlayer'
import ThumbnailBar from '../ThumbnailBar'
import useTouchSlide from '../hooks/useTouchSlide'
import Open3DView from './Open3DView'
import is3DProduct from 'utils/skuMap3DProducts'
import HorizontalThumbnailBar from '../ThumbnailBar/HorizontalThumbnailBar/HorizontalThumbnailBar'
import useTranslations from 'hooks/useTranslations'

type Props = {
  // here the path is only the breadcrumb path
  path: string
  nrOfThumbnails?: number
  openLightbox: () => void
  showGalleryLightbox: boolean
}

export default function ImageGallery(props: Props) {
  const t = useTranslations<'templates-PDP'>()
  const gallery = useGallery()
  const variant = useDisplayVariant()
  const { holder } = useTouchSlide(gallery.index, gallery.setIndex)

  return (
    <Wrapper className="ImageGallery" data-cy-collection="ImageGallery">
      <GalleryContainer className="GalleryContainer">
        <ThumbnailBar gallery={gallery} />
        <div className="main-image-container">
          <MainImage
            ref={holder}
            data-cy-handle="main-image-click"
            tabIndex={gallery.mainImage.type === 'image' ? 0 : -1}
            onClick={() =>
              gallery.mainImage.type === 'image' && props.openLightbox()
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                gallery.mainImage.type === 'image' && props.openLightbox()
              }
            }}
          >
            {gallery.mainImage.type === 'video' ? (
              <WistiaPlayer
                key={gallery.mainImage.wistiaID || ''}
                link={gallery.mainImage.wistiaID || ''}
                data-cy-state="wistia-player"
                autoPlay={false}
                muted={false}
                controlsVisibleOnLoad={true}
                settingsControl={true}
                volumeControl={true}
                fullscreenButton={true}
                playbar={true}
                smallPlayButton={true}
                playButton={true}
              />
            ) : (
              <ProductImage
                img={gallery.mainImage}
                alt={variant.data.title}
                size="l"
                title={t.asText(false, 'open_gallery_fullscreen')}
              />
            )}
          </MainImage>
          <div className="flag-wrapper">
            <Flags product={variant.data} type={'pdp'} />
          </div>
          {is3DProduct(variant.data.sku) && (
            <div className="overlay-layer" data-cy-state="show-3d-btn">
              <Open3DView data-cy-state={'show-3D-Model'}></Open3DView>
            </div>
          )}
        </div>
        <HorizontalThumbnailBar
          thumbnailClick={props.openLightbox}
          gallery={gallery}
          data-cy-state="show-horizona-thumbnail-bar"
        />
      </GalleryContainer>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const GalleryContainer = styled.div`
  gap: ${theme.spacing('m')};
  display: flex;
  flex-direction: column;
  @media (min-width: ${theme.ms.LG}px) {
    flex-direction: row;
    width: 100%;
  }
  > .main-image-container {
    position: relative;
    width: 100%;
    order: 1;
    @media (min-width: ${theme.ms.LG}px) {
      order: 2;
    }

    > .overlay-layer {
      position: absolute;
      bottom: 0;
      right: 0;
    }

    > .flag-wrapper {
      position: absolute;
      top: 0;
      left: 0;
      @media (min-width: ${theme.ms.LG}px) {
        right: 0;
        left: unset;
      }
    }
  }
`

const MainImage = styled.div`
  aspect-ratio: 1 / 1;
`
